import React, {
  FunctionComponent,
  ReactElement,
  useState,
  useRef,
  useEffect,
  useMemo,
} from 'react';
import { Transition } from 'react-transition-group';
import styled from 'styled-components';
import get from 'lodash.get';
import { FormattedMessage } from 'react-intl';
import { useMedia } from 'react-media-match';
import getIcon from '@empiriecom/module-components/utils/getIcon';
import Scroll from '@empiriecom/module-components/Scroll';
import { useCollapsibleContext, Collapsible } from './CollapsibleContext';

export interface CardProps {
  contentPadding?: boolean;
  headline?: string | ReactElement;
  collapsible?: boolean;
  collapsibleId?: string;
  controllable?: boolean;
  scrollTarget?: string;
  collapseLabelText?: string | ReactElement;
  expandLabelText?: string | ReactElement;
  collapseThreshold?: number;
  showCollapseOverlay?: boolean;
}

const iconTransitionStyles = {
  entered: { transform: 'rotate(180deg)' },
  entering: { transform: 'rotate(0deg)' },
  exited: { transform: 'rotate(0deg)' },
  exiting: { transform: 'rotate(180deg)' },
};

const overlayTransitionStyles = {
  entered: { opacity: 0 },
  entering: { opacity: 1 },
  exited: { opacity: 1 },
  exiting: { opacity: 0 },
};

const CardContainer = styled.div<{
  collapseThreshold: number;
  contentHeight: number;
  hasCollapsible: boolean;
  hasHorizontalMargin?: boolean;
  headlineHeight: number;
  mobile: boolean;
  showCollapseOverlay: boolean;
}>`
  position: relative;
  overflow: hidden;
  height: ${({ contentHeight, hasCollapsible, collapseThreshold, showCollapseOverlay }) =>
    hasCollapsible && contentHeight >= collapseThreshold
      ? `calc(${contentHeight}px ${showCollapseOverlay ? '+ 9rem' : '+ 5rem'})`
      : 'auto'};
  transition: height 0.3s ease-out;
  will-change: height;
  .collapseButton {
    border-top: 0px none;
  }
  &&.collapsed {
    height: ${({ contentHeight, collapseThreshold, headlineHeight, mobile }) =>
      contentHeight >= collapseThreshold
        ? `calc(${collapseThreshold + headlineHeight}px + ${mobile ? '4.5rem' : '3.25rem'})`
        : 'auto'};
  }
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 3px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.2), 0 2px 1px -1px rgba(0, 0, 0, 0.12),
    0 1px 1px 0 rgba(0, 0, 0, 0.14);
  margin: 0.75rem;
`;

export const CardHeadline = styled.div`
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.75;
  padding: 0.25rem 1rem;
`;

const CardContent = styled.div<{ contentPadding: boolean }>`
  ${({ contentPadding }) => contentPadding && 'padding: 1rem;'}
`;

const HeadlineContainer = styled.div`
  border-bottom: 1px solid #e0e0e0;
`;

const CollapseOverlay = styled.div`
  position: absolute;
  bottom: 0;
  width: 100%;
  height: 8rem;
  z-index: 1;
  background: linear-gradient(to top, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0) 100%);
  transition: opacity 0.5s ease-in-out;
  will-change: opacity;
  opacity: 1;
`;

const CollapseButton = styled.div`
  position: absolute;
  border-top: 1px solid #f4f4f4;
  width: 100%;
  height: 3.25rem;
  background-color: #ffffff;
  bottom: 0;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  cursor: pointer;
  z-index: 2;
  padding: 0 1rem;
`;

const CollapseIconWrapper = styled.span`
  width: 1rem;
  margin-left: 0.75rem;
  transition: all 500ms ease-in-out;
`;

const CardScrollLink = styled.span`
  display: block;
`;

/**
 * <p>This is the Card component. It renders a visually enclosed area with a configurable elevation level.</p>
 * <p>An optional card headline can be provided. Also the content padding can be toggled via the `contentPadding`
 * property.</p>
 * <p>If the `collapsible` flag is set to true the card will become collapsible if the content height of the card
 * content is either `&gt; 400px` or greater than the passed `collapseThreshold`.</p>
 */
export const Card: FunctionComponent<CardProps> = ({
  children,
  contentPadding = false,
  headline,
  collapsible = false,
  scrollTarget = '',
  collapseLabelText = '',
  collapsibleId = '',
  controllable = false,
  expandLabelText = '',
  collapseThreshold = 400,
  showCollapseOverlay = true,
}) => {
  const { addCollapsible, getCollapsibles } = useCollapsibleContext();
  const [isCollapsed, setIsCollapsed] = useState<boolean>(true);
  const content = useRef(null);
  const headlineRef = useRef(null);
  const [height, setHeight] = useState<number>(0);
  const [headlineHeight, setHeadlineHeight] = useState<number>(0);
  const ArrowDown = getIcon('ArrowMediumDown');
  const isMobile = useMedia({ desktop: false, mobile: true, tablet: false });

  useEffect(() => setHeight(get(content, 'current.clientHeight')));
  useEffect(() => setHeadlineHeight(get(headlineRef, 'current.clientHeight')));
  useEffect(() => {
    if (controllable) {
      const controlledCollapsible: Collapsible = {
        collapsed: isCollapsed,
        identifier: collapsibleId,
      };
      addCollapsible(controlledCollapsible);
      setIsCollapsed(controlledCollapsible.collapsed);
    }
  }, []);

  useMemo(() => {
    if (controllable) {
      const toCheck = getCollapsibles().filter(
        (cllpsbl) => cllpsbl.identifier === collapsibleId,
      )[0];
      if (typeof toCheck !== 'undefined' && typeof toCheck.collapsed !== 'undefined') {
        if (toCheck.collapsed !== isCollapsed) {
          setIsCollapsed(toCheck.collapsed);
        }
      }
    }
  }, [getCollapsibles()]);

  const CollapseButtonElement = (
    <>
      <CollapseButton onClick={() => setIsCollapsed(!isCollapsed)}>
        {isCollapsed &&
          (expandLabelText !== '' ? (
            expandLabelText
          ) : (
            <FormattedMessage
              id="product-details.expand"
              defaultMessage="Mehr Produkteigenschaften"
            />
          ))}
        {!isCollapsed &&
          (collapseLabelText !== '' ? (
            collapseLabelText
          ) : (
            <FormattedMessage
              id="product-details.collapse"
              defaultMessage="Weniger Produkteigenschaften"
            />
          ))}
        <Transition in={!isCollapsed} timeout={0}>
          {(state) => (
            <CollapseIconWrapper style={{ ...iconTransitionStyles[state] }}>
              <ArrowDown size="1rem" />
            </CollapseIconWrapper>
          )}
        </Transition>
      </CollapseButton>
      {showCollapseOverlay && (
        <Transition in={!isCollapsed} timeout={0}>
          {(stt) => <CollapseOverlay style={{ ...overlayTransitionStyles[stt] }} />}
        </Transition>
      )}
    </>
  );

  return (
    <CardContainer
      className={collapsible && isCollapsed ? 'collapsible collapsed' : 'collapsible'}
      hasCollapsible={collapsible}
      contentHeight={height}
      headlineHeight={headlineHeight}
      mobile={isMobile}
      collapseThreshold={collapseThreshold}
      showCollapseOverlay={showCollapseOverlay}
    >
      {headline && (
        <HeadlineContainer ref={headlineRef}>
          {typeof headline === 'string' ? <CardHeadline>{headline}</CardHeadline> : headline}
        </HeadlineContainer>
      )}
      <CardContent
        contentPadding={contentPadding}
        data-testid="card-content"
        onError={() => setHeight(get(content, 'current.clientHeight'))}
        onLoad={() => {
          setHeight(get(content, 'current.clientHeight'));
        }}
        ref={content}
      >
        {children}
      </CardContent>
      {collapsible && height >= collapseThreshold && (
        <>
          {!isCollapsed && scrollTarget !== '' ? (
            <Scroll
              render={(scroll) => (
                <CardScrollLink onClick={scroll}>{CollapseButtonElement}</CardScrollLink>
              )}
              positionAfterScroll={scrollTarget}
            />
          ) : (
            CollapseButtonElement
          )}
        </>
      )}
    </CardContainer>
  );
};

interface SectionProps {
  headline: string | ReactElement;
}

const SectionHeadline = styled.div<{ isMobile: boolean }>`
  background: #fff;
  font-size: 1.5rem;
  padding: 1rem;
  ${({ isMobile }) => isMobile && 'text-align: center;'}
`;

const SectionContent = styled.div`
  border: 1px solid #f4f4f4;
  background: #f4f4f4;
  padding: 0;
`;

export const Section: FunctionComponent<SectionProps> = ({ children, headline }) => {
  const isMobile = useMedia({ desktop: false, mobile: true, tablet: false });

  return (
    <div>
      <SectionHeadline isMobile={isMobile}>{headline}</SectionHeadline>
      <SectionContent>{children}</SectionContent>
    </div>
  );
};

export default Card;
